import { NextRequest, NextResponse } from 'next/server'
import { and, eq } from 'drizzle-orm'
import { db } from '@/db'
import { auditLog, tickets } from '@/db/schema'
import { requireExpertApiUser } from '@/lib/role-guard'

const MAX_TAGS = 10
const MAX_TAG_LENGTH = 60

export async function PATCH(
  request: NextRequest,
  { params }: { params: Promise<{ ticketId: string }> },
) {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const { ticketId } = await params
  const body = await request.json().catch(() => null)

  if (!body || !Array.isArray(body.tags)) {
    return NextResponse.json({ error: 'tags must be an array' }, { status: 400 })
  }

  const rawTags: unknown[] = body.tags
  if (rawTags.some((t) => typeof t !== 'string')) {
    return NextResponse.json({ error: 'each tag must be a string' }, { status: 400 })
  }

  const tags = (rawTags as string[])
    .map((t) => t.toLowerCase().trim())
    .filter((t) => t.length > 0 && t.length <= MAX_TAG_LENGTH)
    .slice(0, MAX_TAGS)

  const [ticket] = await db
    .select({ id: tickets.id, categoryTags: tickets.categoryTags })
    .from(tickets)
    .where(and(eq(tickets.id, ticketId), eq(tickets.tenantId, guard.user.tenantId)))
    .limit(1)

  if (!ticket) return NextResponse.json({ error: 'Ticket not found' }, { status: 404 })

  await db
    .update(tickets)
    .set({ categoryTags: tags, updatedAt: new Date() })
    .where(eq(tickets.id, ticketId))

  await db.insert(auditLog).values({
    tenantId: guard.user.tenantId,
    actorId: guard.user.id,
    actorType: 'user',
    action: 'ticket.tags_updated',
    entityType: 'ticket',
    entityId: ticketId,
    payload: { from: ticket.categoryTags, to: tags },
  })

  return NextResponse.json({ ticketId, tags })
}
